import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import TradeInPricingTabs from '../../components/TradeInPricingTabs';
import AlertNotification from '../../components/AlertNotification';
import SearchBox from '../../components/SearchBox';
import Pagination from '../../components/Pagination';
import FilterDrawer from '../../components/FilterDrawer';
import { formatDateTime } from '../../utils/formateDate';
import noBrandImage from '../../assets/img/brand/noImage.png';
import emptyStateImg from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import SortableHeader from '../../components/SortableHeader';
import apiHelper from '../../apiHelper';
import ListLoadError from '../../components/ListLoadError';
import { renumberFormulaRefs, deductionDisplayNumbers } from '../../utils/deductionFormula';

const PH_PAGE_SIZE = 20;

// TEMP: hardcoded records to preview the UI — set to false to fetch from the API.
const USE_MOCK_ROWS = false;

const MOCK_ROWS = [
    {
        id: 1,
        changesTime: '2026-03-05T18:30:00',
        initiator: 'John Doe',
        listingModel: 'iPhone 16 Pro Max',
        listingItem: '8/128GB',
        image: null,
        color: 'Desert',
        colorCode: '#C9A27E',
        condition: 'New',
        pricingType: 'Normal',
        campaignName: null,
        campaignPeriod: null,
        priceBefore: '1500.00',
        priceAfter: '1800.00',
        hasDeductionChange: true,
        deductions: [
            { deduction_item_name: 'Sealed and Not Activated', data_type_before: 'Numeric', data_type_after: 'Numeric', deduction_value_before: '0', deduction_value_after: '-100', has_change: true },
            { deduction_item_name: 'Opened and Activated Within 3 Days', data_type_before: 'Numeric', data_type_after: 'Formula', deduction_value_before: '-100', formula_after: '{1}-20', has_change: true },
            { deduction_item_name: 'Opened and Activated Within 7 Days', data_type_before: 'Numeric', data_type_after: 'Numeric', deduction_value_before: '-150', deduction_value_after: '-150', has_change: false },
            { deduction_item_name: 'Opened and Activated Within 14 Days', data_type_before: 'Formula', data_type_after: 'Numeric', formula_before: '{1}-150', deduction_value_after: '-100', has_change: true },
            { deduction_item_name: 'With Official Receipt', data_type_before: 'N/A', data_type_after: 'WhatsApp', deduction_value_before: 'N/A', deduction_value_after: 'WhatsApp', has_change: true },
            { deduction_item_name: 'Without Official Receipt', has_change: false },
            { deduction_item_name: 'Local Set', has_change: false },
            { deduction_item_name: 'Export Set', has_change: false },
            { deduction_item_name: 'Flawless', has_change: false },
            { deduction_item_name: 'Minor Scratches', has_change: false },
            { deduction_item_name: 'Moderate scratches', has_change: false },
            { deduction_item_name: 'Cracked or chipped', has_change: false },
        ],
    },
    {
        id: 2,
        changesTime: '2026-03-05T18:30:00',
        initiator: 'John Doe',
        listingModel: 'iPhone 16 Pro Max',
        listingItem: '8/128GB',
        image: null,
        color: 'Blue',
        colorCode: '#3B5B92',
        condition: 'New',
        pricingType: 'Normal',
        campaignName: null,
        campaignPeriod: null,
        priceBefore: '1500.00',
        priceAfter: '1800.00',
        hasDeductionChange: true,
        deductions: [
            { deduction_item_name: 'Cracked or Chipped Exterior', deduction_value_before: '-150.00', deduction_value_after: '-200.00', has_change: true },
        ],
    },
    {
        id: 3,
        changesTime: '2026-03-05T18:30:00',
        initiator: 'John Doe',
        listingModel: 'iPhone 16 Pro Max',
        listingItem: '8/128GB',
        image: null,
        color: 'White',
        colorCode: '#F5F5F0',
        condition: 'New',
        pricingType: 'Normal',
        campaignName: null,
        campaignPeriod: null,
        priceBefore: '1500.00',
        priceAfter: '1800.00',
        hasDeductionChange: false,
        deductions: [],
    },
    {
        id: 4,
        changesTime: '2026-03-05T12:00:00',
        initiator: 'Jess Yap',
        listingModel: 'Samsung Galaxy S26 Plus',
        listingItem: '256GB',
        image: null,
        color: 'Silver White',
        colorCode: '#D8D8D8',
        condition: 'New',
        pricingType: 'Promotional',
        campaignName: 'March Madness Trade-Up',
        campaignStatus: 'Ongoing',
        campaignEvent: 'start',
        campaignPeriod: { start: '2026-03-05T12:00:00', end: '2026-03-19T23:59:00' },
        priceBefore: '1200.00',
        priceAfter: '1000.00',
        hasDeductionChange: false,
        deductions: [],
    },
    {
        id: 5,
        changesTime: '2026-03-05T12:00:00',
        initiator: 'Jess Yap',
        listingModel: 'Samsung Galaxy S26 Plus',
        listingItem: '256GB',
        image: null,
        color: 'Pink Gold',
        colorCode: '#E8B4B8',
        condition: 'Used',
        pricingType: 'Promotional',
        campaignName: 'CNY Flash Trade-In',
        campaignStatus: 'Ended-On schedule',
        campaignEvent: 'end',
        campaignPeriod: { start: '2026-02-10T00:00:00', end: '2026-02-20T23:59:00' },
        priceBefore: '1200.00',
        priceAfter: '1000.00',
        hasDeductionChange: true,
        deductions: [
            { deduction_item_name: 'Cracked or Chipped Screen', deduction_value_before: '-200.00', deduction_value_after: '-250.00', has_change: true },
            { deduction_item_name: 'Battery Service', deduction_value_before: 'N/A', deduction_value_after: 'N/A', has_change: false },
        ],
    },
    {
        id: 6,
        changesTime: '2026-03-12T09:15:00',
        initiator: 'Jess Yap',
        listingModel: 'iPhone 15',
        listingItem: '128GB',
        image: null,
        color: 'Blue',
        colorCode: '#A7C7E7',
        condition: 'Used',
        pricingType: 'Promotional',
        campaignName: 'Apple Promotion',
        campaignStatus: 'Ongoing',
        campaignEvent: 'remove',
        campaignPeriod: { start: '2026-03-05T12:00:00', end: '2026-03-19T23:59:00' },
        priceBefore: '1000.00',
        priceAfter: '1200.00',
        hasDeductionChange: false,
        deductions: [],
    },
];

function normalizeColorCode(code) {
    if (!code) return '#cccccc';
    return code.startsWith('#') ? code : `#${code}`;
}

function mapHistory(records) {
    return records.map(r => ({
        id:                 r.tradein_pricing_history_id,
        changesTime:        r.changes_time,
        initiator:          r.initiator,
        listingModel:       r.listing_model,
        listingItem:        r.listing_item,
        image:              r.listing_item_image,
        color:              r.color || '',
        colorCode:          normalizeColorCode(r.color_code),
        condition:          r.condition,
        pricingType:        r.pricing_type,
        campaignName:       r.campaign_name,
        campaignStatus:     r.campaign_status,
        campaignEvent:      r.campaign_event,   // 'start' | 'end' | 'remove'
        campaignPeriod:     r.campaign_period,
        priceBefore:        r.base_price_before,
        priceAfter:         r.base_price_after,
        hasDeductionChange: r.has_deduction_change,
        deductions:         numberDeductions(r.deductions || []),
    }));
}

// ERP-5301: the drawer's ID is a deduction's place in the list (its definition's sort_order),
// and formulas are shown in those numbers — they're stored by definition id, which no longer
// matches once "Opened and Not Activated" (id 32) sits 2nd. `no` is left unset when a record
// can't be numbered (a definition since removed), and the drawer falls back to the row index.
function numberDeductions(deductions) {
    const numbers = deductionDisplayNumbers(deductions
        .filter(d => d.deduction_id != null)
        .map(d => [d.deduction_id, d.sort_order]));
    if (!numbers) return deductions;
    return deductions.map(d => ({
        ...d,
        no:             numbers[d.deduction_id],
        formula_before: renumberFormulaRefs(d.formula_before, numbers),
        formula_after:  renumberFormulaRefs(d.formula_after, numbers),
    }));
}

// Numeric price → "SGD 1,500.00"; text price (e.g. "WhatsApp") passes through.
function isNumeric(v) {
    return v !== null && v !== undefined && v !== '' && /^-?\d+(\.\d+)?$/.test(String(v).trim());
}

function fmtPrice(v) {
    if (v === null || v === undefined || v === '') return '—';
    if (isNumeric(v)) return `SGD ${parseFloat(v).toLocaleString('en-SG', { minimumFractionDigits: 2 })}`;
    return v;
}

// Deduction "Value Changes": for numeric values drop the decimal part entirely
// (truncate toward zero, no rounding). Formulas / text (e.g. "{1}-20", "N/A",
// "WhatsApp") are left untouched.
function fmtDeductionValue(v) {
    if (v == null) return v;
    const s = String(v).trim();
    return isNumeric(s) ? String(Math.trunc(parseFloat(s))) : s;
}

// Renders one side (before/after) of a deduction "Value Changes" cell.
// Formula → "formula, result" (comma-separated, its computed result); if the result
// isn't available the formula shows alone (no "(N/A)"). Numeric → decimals dropped.
function fmtDeductionSide(dataType, value, formula, formulaResult) {
    const typeIsFormula = dataType != null && String(dataType).toLowerCase() === 'formula';
    const hasFormula = formula != null && String(formula).trim() !== '';
    if (typeIsFormula || hasFormula) {
        const f = hasFormula ? String(formula).trim() : '';
        const hasResult = formulaResult != null && String(formulaResult).trim() !== '';
        return hasResult ? `${f}, ${fmtDeductionValue(formulaResult)}` : f;
    }
    return value != null ? fmtDeductionValue(value) : null;
}

// Whether a formula's computed result differs before→after. A formula whose text
// is unchanged can still change value when a column it references was edited
// (e.g. col2 = {col1}-10, and col1 went 50→100 so col2 goes 40→90). Numeric-aware
// so "40" and "40.00" are treated as equal.
function formulaResultChanged(before, after) {
    if (before == null && after == null) return false;
    if (isNumeric(before) && isNumeric(after)) return parseFloat(before) !== parseFloat(after);
    return String(before ?? '').trim() !== String(after ?? '').trim();
}

// A deduction row is a real change only when its data type, its value/formula, or
// its computed formula result actually differs before→after (same logic the drawer
// uses to render changes). Derived from the data rather than trusting the backend
// has_change flag. The formula-result comparison surfaces dependent-column changes:
// a column whose value changed because another column it references was edited.
function deductionHasRealChange(d) {
    if (!d) return false;
    const typeChanged = (d.data_type_before || d.data_type_after) && d.data_type_before !== d.data_type_after;
    const valBefore = d.deduction_value_before ?? d.formula_before;
    const valAfter  = d.deduction_value_after  ?? d.formula_after;
    const valChanged = (valBefore != null || valAfter != null) && valBefore !== valAfter;
    const resultChanged = formulaResultChanged(d.formula_result_before, d.formula_result_after);
    return !!(typeChanged || valChanged || resultChanged);
}

// A non-empty, non-numeric base value (e.g. "WhatsApp"). A change involving one of
// these on either side can't be shown as a numeric transition, so the cell shows "-".
function isTextValue(v) {
    return v != null && String(v).trim() !== '' && !isNumeric(v);
}

// Whether the base price value actually changed (numeric compare when both numeric,
// otherwise a string compare). Drives whether the row is worth showing.
function baseHasRealChange(row) {
    const bothNumeric = isNumeric(row.priceBefore) && isNumeric(row.priceAfter);
    return bothNumeric
        ? parseFloat(row.priceBefore) !== parseFloat(row.priceAfter)
        : String(row.priceBefore ?? '').trim() !== String(row.priceAfter ?? '').trim();
}

// A history row is only shown when something actually changed — either the base
// price or at least one deduction. Rows with no change at all are hidden.
function rowHasAnyChange(row) {
    return baseHasRealChange(row) || (row.deductions || []).some(deductionHasRealChange);
}

// Shared styling for every "-" placeholder shown in the Pricing History table.
const DASH_STYLE = {
    color: '#212529',
    fontFamily: 'Montserrat',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
    display: 'inline-block',
    width: 5,
};
const Dash = (props) => <span style={{ ...DASH_STYLE, ...(props.style || {}) }}>-</span>;

function buildPHCacheKey(page, search, filter, sf, so) {
    return JSON.stringify({
        page,
        search: (search || '').trim(),
        filter: filter ? {
            start:       filter.changesTime_start ? new Date(filter.changesTime_start).toISOString() : null,
            end:         filter.changesTime_end   ? new Date(filter.changesTime_end).toISOString()   : null,
            initiator:   (filter.initiator || []).slice().sort(),
            condition:   (filter.condition || []).slice().sort(),
            pricingType: (filter.pricingType || []).slice().sort(),
        } : null,
        sf: sf || null,
        so,
    });
}

function buildPHParams(page, search, filter, sf, so) {
    const params = new URLSearchParams({ page, page_size: PH_PAGE_SIZE });
    const s = (search || '').trim();
    if (s) params.append('search', s);
    if (filter?.changesTime_start) params.append('changes_time_start', new Date(filter.changesTime_start).toISOString());
    if (filter?.changesTime_end)   params.append('changes_time_end',   new Date(filter.changesTime_end).toISOString());
    if (filter?.initiator?.length)   params.append('initiator', filter.initiator.join(','));
    if (filter?.condition?.length)   params.append('condition', filter.condition.join(','));
    if (filter?.pricingType?.length) params.append('pricing_type', filter.pricingType.join(','));
    const SORT_FIELDS = ['time', 'initiator', 'listingModel', 'listingItem', 'condition', 'pricingType'];
    if (sf && SORT_FIELDS.includes(sf)) {
        params.append('ordering', `${so === 'desc' ? '-' : ''}${sf}`);
    } else {
        params.append('ordering', '-time');
    }
    return params.toString();
}

function PhoneImage({ src, size = 32 }) {
    return (
        <img
            src={src || noBrandImage}
            alt=""
            style={{
                width: size, height: size,
                objectFit: 'cover', flexShrink: 0,
                border: '1px solid #E9ECEF', background: '#F1F3F5',
            }}
        />
    );
}

const PricingTypeBadge = ({ isPromotion }) => {
    const s = isPromotion
        ? { bg: 'var(--Utilised-blue-1, #CFE2FF)', color: 'var(--Utilised-blue-2, #2C7BE5)', border: 'var(--Utilised-blue-2, #2C7BE5)' }
        : { bg: 'var(--Utilised-background-2, #F8F9FA)', color: 'var(--Utilised-text-2, #6C757D)', border: 'var(--Utilised-text-2, #6C757D)' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '97px', height: '20px',
            padding: '2px 10px', borderRadius: '99px',
            fontSize: '11px', fontWeight: 600, lineHeight: 'normal',
            fontFamily: 'Montserrat', textAlign: 'center',
            background: s.bg, color: s.color,
            border: `1px solid ${s.border}`, whiteSpace: 'nowrap',
            boxSizing: 'border-box', flexShrink: 0,
        }}>
            {isPromotion ? 'Promotional' : 'Normal'}
        </span>
    );
};

const ArrowRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
        <path d="M2.5 6H9.5M6 9.5L9.5 6L6 2.5" stroke="#212529" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PromoInfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
        <path fillRule="evenodd" clipRule="evenodd" d="M5 9.375C6.16032 9.375 7.27312 8.91406 8.09359 8.09359C8.91406 7.27312 9.375 6.16032 9.375 5C9.375 3.83968 8.91406 2.72688 8.09359 1.90641C7.27312 1.08594 6.16032 0.625 5 0.625C3.83968 0.625 2.72688 1.08594 1.90641 1.90641C1.08594 2.72688 0.625 3.83968 0.625 5C0.625 6.16032 1.08594 7.27312 1.90641 8.09359C2.72688 8.91406 3.83968 9.375 5 9.375ZM5 10C6.32608 10 7.59785 9.47322 8.53553 8.53553C9.47322 7.59785 10 6.32608 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784 6.32608 0 5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5C0 6.32608 0.526784 7.59785 1.46447 8.53553C2.40215 9.47322 3.67392 10 5 10Z" fill="#5E6E82"/>
        <path d="M5.58122 4.11743L4.14997 4.29681L4.09872 4.53431L4.37997 4.58618C4.56372 4.62993 4.59997 4.69618 4.55997 4.87931L4.09872 7.04681C3.97747 7.60743 4.16435 7.87118 4.60372 7.87118C4.94435 7.87118 5.33997 7.71368 5.51935 7.49743L5.57434 7.23743C5.44934 7.34743 5.26684 7.39118 5.1456 7.39118C4.97372 7.39118 4.91122 7.27056 4.9556 7.05806L5.58122 4.11743Z" fill="#5E6E82"/>
        <path d="M5 3.4375C5.34518 3.4375 5.625 3.15768 5.625 2.8125C5.625 2.46732 5.34518 2.1875 5 2.1875C4.65482 2.1875 4.375 2.46732 4.375 2.8125C4.375 3.15768 4.65482 3.4375 5 3.4375Z" fill="#5E6E82"/>
    </svg>
);

// The tooltip chip shows only the campaign event: Start (green, same as the
// "Ongoing" badge), End (grey, same as the "Ended-On schedule" badge) or Remove
// (red, for an item taken out of a campaign that was still running). A removal
// reverts the item to its normal price just like an end does, so the row itself
// renders identically — the chip is the only thing that tells them apart.
const CAMPAIGN_EVENT_CONFIG = {
    start:  { label: 'Start',  bg: '#D1E7DD', color: '#198754', border: '1px solid #198754' },
    end:    { label: 'End',    bg: '#E9ECEF', color: '#212529', border: '1px solid #212529' },
    remove: { label: 'Remove', bg: '#F8D7DA', color: '#B02A37', border: '1px solid #B02A37' },
};

const CampaignStatusChip = ({ event }) => {
    const cfg = CAMPAIGN_EVENT_CONFIG[event];
    if (!cfg) return null;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
            fontFamily: 'Montserrat', background: cfg.bg, color: cfg.color,
            border: cfg.border, whiteSpace: 'nowrap', width: 'fit-content',
        }}>
            {cfg.label}
        </span>
    );
};

// position:fixed (anchored off the icon's bounding rect) rather than absolute: the
// table sits in an overflowX:auto wrapper, which also clips vertically, so an absolute
// popup above the row was cut off and never appeared.
const PromoPopup = ({ campaignName, campaignEvent, pos }) => (
    <div style={{
        position: 'fixed', bottom: pos.bottom, left: pos.left,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: '8px',
        border: '1px solid var(--Background-Gray, #E9ECEF)',
        background: 'var(--Gray-100, #F8F9FA)',
        display: 'inline-flex', alignItems: 'center',
        padding: '10px', gap: '8px',
        zIndex: 99999, pointerEvents: 'none',
        whiteSpace: 'nowrap',
        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
    }}>
        <span style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 400, color: '#212529' }}>
            <span style={{ fontWeight: 700 }}>Campaign: </span>{campaignName || '—'}
        </span>
        <CampaignStatusChip event={campaignEvent} />
    </div>
);

const FilterInactiveIcon = () => (
    <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const FilterDisabledIcon = () => (
    <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#CED4DA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#CED4DA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const FilterActiveIcon = () => (
    <svg style={{ margin: 0 }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <g clipPath="url(#clip_ph_fa)">
            <path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" />
        </g>
        <defs>
            <clipPath id="clip_ph_fa">
                <rect width="12" height="12" fill="white" transform="translate(11 5)" />
            </clipPath>
        </defs>
    </svg>
);

const ViewDetailsLink = ({ onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            background: 'none', border: 'none', padding: 0,
            color: '#0D6EFD', fontFamily: 'Montserrat',
            fontSize: 13, fontWeight: 500, cursor: 'pointer',
            textDecoration: 'underline', whiteSpace: 'nowrap',
            display: 'inline-flex', alignItems: 'center', gap: 4,
        }}
    >
        View Details
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="15 3 21 3 21 9" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="10" y1="14" x2="21" y2="3" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </button>
);

export default function PricingHistory() {
    const location = useLocation();
    const [notification, setNotification] = useState({ show: false, message: '' });

    useEffect(() => {
        if (location.state?.successMessage) {
            setNotification({ show: true, message: location.state.successMessage });
            window.history.replaceState({}, document.title);
        }
    }, []); // eslint-disable-line

    // ── Data state ──
    const [rows,       setRows]       = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading,  setIsLoading]  = useState(true);
    // Set when the history request fails; shows "Couldn't load" with Try again
    const [loadError,  setLoadError]  = useState(false);

    const [colWidths, setColWidths] = useState({
        changesTime: 150,
        initiator: 130,
        listingModel: 170,
        listingItem: 190,
        condition: 110,
        pricingType: 130,
        basePriceChanges: 180,
        deductionChanges: 150,
    });

    const handleColResizeMouseDown = (e, key, startWidth, minWidth = 90) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const onMouseMove = (moveEvent) => {
            const delta = moveEvent.clientX - startX;
            setColWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, startWidth + delta) }));
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const ColResizeHandle = ({ colKey, minWidth }) => (
        <div
            className="col-resize-handle"
            onMouseDown={(e) => handleColResizeMouseDown(e, colKey, colWidths[colKey], minWidth)}
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '6px', cursor: 'col-resize', zIndex: 2, userSelect: 'none' }}
        />
    );

    // ── Search ──
    const [phSearchInput,   setPhSearchInput]   = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // ── Pagination ──
    const [phCurrentPage, setPhCurrentPage] = useState(1);

    // ── Filter ──
    const [phAppliedFilter,   setPhAppliedFilter]   = useState(null);
    const [phFilterPopupOpen, setPhFilterPopupOpen] = useState(false);
    const [initiatorOptions,  setInitiatorOptions]  = useState([]);
    const [phTempValues,      setPhTempValues]      = useState({
        changesTime_start: null,
        changesTime_end:   null,
        initiator:         [],
        initiatorSearch:   '',
        condition:         [],
        pricingType:       [],
    });

    // ── Initiator options for the filter drawer ──
    useEffect(() => {
        if (USE_MOCK_ROWS) {
            setInitiatorOptions([{ id: 1, name: 'John Doe' }, { id: 2, name: 'Jess Yap' }]);
            return;
        }
        (async () => {
            try {
                const res = await apiHelper('sales/trade-in-pricing-history/filter-options/');
                if (res?.ok) {
                    const data = await res.json();
                    setInitiatorOptions(data.initiators ?? []);
                }
            } catch (_) {}
        })();
    }, []);

    // ── Sort ──
    const [phSortField, setPhSortField] = useState('time');
    const [phSortOrder, setPhSortOrder] = useState('desc');

    // ── Prefetch cache refs ──
    const prefetchCache      = useRef(new Map());
    const prefetchInProgress = useRef(new Set());
    const isFirstRender      = useRef(true);
    // Numbers each list request, so a slower older response can't replace a newer one
    const loadSeq            = useRef(0);

    // ── Deduction drawer ──
    const [selectedRow, setSelectedRow] = useState(null);
    const [drawerOpen,  setDrawerOpen]  = useState(false);
    const drawerRef = useRef(null);

    // ── Promo info popup ──
    // { id, left, bottom, campaignName, campaignEvent, pinned }. Hover opens it;
    // clicking the icon pins it open (people expect the ⓘ to be clickable), and a
    // pinned popup closes on a second click, an outside click, Escape, or scroll.
    const [promoTip, setPromoTip] = useState(null);

    const openPromoTip = (el, row, pinned) => {
        const r = el.getBoundingClientRect();
        setPromoTip({
            id: row.id,
            left: r.left,
            bottom: window.innerHeight - r.top + 8,
            campaignName: row.campaignName,
            campaignEvent: row.campaignEvent,
            pinned,
        });
    };

    const promoTipPinned = !!promoTip?.pinned;
    useEffect(() => {
        if (!promoTipPinned) return;
        const onDocMouseDown = (e) => {
            if (e.target.closest?.('[data-promo-anchor]')) return; // the icon toggles itself
            setPromoTip(null);
        };
        const onKeyDown = (e) => { if (e.key === 'Escape') setPromoTip(null); };
        // The popup is fixed-positioned, so it would drift away from its icon on scroll.
        const onScrollOrResize = () => setPromoTip(null);
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [promoTipPinned]);

    const isPhInitialTableEmpty = !isLoading && !loadError && totalCount === 0 && !debouncedSearch && !phAppliedFilter;
    const phTotalPages = Math.ceil(totalCount / PH_PAGE_SIZE);

    const phSections = [
        { label: 'Changes Time', type: 'dateRangeWithTime', key: 'changesTime' },
        {
            label: 'Initiator', type: 'list', key: 'initiator',
            data: initiatorOptions,
            renderItem: (u) => ({ id: u.id, title: u.name }),
            showDropdownIcon: true,
        },
        { label: 'Condition', type: 'checkbox', key: 'condition', options: ['New', 'Used'] },
        { label: 'Pricing Type', type: 'checkbox', key: 'pricingType', options: ['Normal', 'Promotional'] },
    ];

    const handlePhSort = (col) => {
        if (phSortField === col) {
            setPhSortOrder(o => o === 'asc' ? 'desc' : 'asc');
        } else {
            setPhSortField(col);
            setPhSortOrder('asc');
        }
        setPhCurrentPage(1);
    };

    // Search fires only on Enter (or clear) — SearchBox calls setSearchTerm itself.

    // ── Clear cache on filter/sort change ──
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }
        prefetchCache.current.clear();
        prefetchInProgress.current.clear();
    }, [debouncedSearch, phAppliedFilter, phSortField, phSortOrder]); // eslint-disable-line

    // ── Prefetch helper ──
    const prefetchPHPage = async (page, search, filter, sf, so, totalCnt) => {
        const totalPgs = Math.ceil(totalCnt / PH_PAGE_SIZE);
        if (page > totalPgs) return;
        const key = buildPHCacheKey(page, search, filter, sf, so);
        if (prefetchCache.current.has(key) || prefetchInProgress.current.has(key)) return;
        prefetchInProgress.current.add(key);
        try {
            const res = await apiHelper(`sales/trade-in-pricing-history/?${buildPHParams(page, search, filter, sf, so)}`);
            if (res?.ok) {
                const data = await res.json();
                prefetchCache.current.set(key, {
                    rows:  mapHistory(data.results ?? []),
                    count: data.count ?? 0,
                });
            }
        } catch (_) {}
        finally { prefetchInProgress.current.delete(key); }
    };

    // ── Cache-aware fetch ──
    const fetchPHPage = async (page, search, filter, sf, so) => {
        if (USE_MOCK_ROWS) {
            setRows(MOCK_ROWS);
            setTotalCount(MOCK_ROWS.length);
            setIsLoading(false);
            return;
        }
        // Taken before the cache check, so a cached page also cancels older requests
        const seq    = ++loadSeq.current;
        const key    = buildPHCacheKey(page, search, filter, sf, so);
        const cached = prefetchCache.current.get(key);
        if (cached) {
            setRows(cached.rows);
            setTotalCount(cached.count);
            // An older request may still be loading; this page is the one to show
            setIsLoading(false);
            setLoadError(false);
            prefetchPHPage(page + 1, search, filter, sf, so, cached.count);
            return;
        }
        setIsLoading(true);
        try {
            const res = await apiHelper(`sales/trade-in-pricing-history/?${buildPHParams(page, search, filter, sf, so)}`);
            if (seq !== loadSeq.current) return;
            if (res?.ok) {
                const data  = await res.json();
                // A newer page/search/filter/sort request has started; drop this response
                if (seq !== loadSeq.current) return;
                const mapped = mapHistory(data.results ?? []);
                const count  = data.count ?? 0;
                prefetchCache.current.set(key, { rows: mapped, count });
                setLoadError(false);
                setRows(mapped);
                setTotalCount(count);
                prefetchPHPage(page + 1, search, filter, sf, so, count);
            } else {
                // A failed request is not an empty history
                setLoadError(true); setRows([]); setTotalCount(0);
            }
        } catch (_) {
            if (seq === loadSeq.current) { setLoadError(true); setRows([]); setTotalCount(0); }
        } finally {
            if (seq === loadSeq.current) setIsLoading(false);
        }
    };

    // ── Reactive fetch ──
    useEffect(() => {
        fetchPHPage(phCurrentPage, debouncedSearch, phAppliedFilter, phSortField, phSortOrder);
    }, [phCurrentPage, debouncedSearch, phAppliedFilter, phSortField, phSortOrder]); // eslint-disable-line

    return (
        <div className="company-body new-look stock-wrap bg-checkbox">
            <TradeInPricingTabs />
            <div className="employee-tabs-container">
                {notification.show && (
                    <AlertNotification
                        type="success"
                        recordName=""
                        action="create"
                        message={notification.message}
                        onClose={() => setNotification({ show: false, message: '' })}
                    />
                )}
                <div className="tab-content" id="price-history-content">
                    <div className="info-section">
                        <div className="info-card">
                            <div className="entities-tab">
                                <div className="toolbar">
                                    <div className="toolbar-left">
                                        <SearchBox
                                            searchInput={phSearchInput}
                                            setSearchInput={setPhSearchInput}
                                            setSearchTerm={setDebouncedSearch}
                                            setCurrentPage={setPhCurrentPage}
                                            disabled={isPhInitialTableEmpty}
                                        />
                                        <button
                                            className={`icon-btn p-2 filter-btn ${phAppliedFilter ? 'applied' : ''}`}
                                            onClick={() => !isPhInitialTableEmpty && setPhFilterPopupOpen(prev => !prev)}
                                            disabled={isPhInitialTableEmpty}
                                            style={{
                                                border: isPhInitialTableEmpty
                                                    ? '1px solid var(--Gray-400, #CED4DA)'
                                                    : phAppliedFilter ? '1px solid #2C7BE5' : '1px solid #6C757D',
                                                borderWidth: '1px',
                                                color: isPhInitialTableEmpty ? '#CED4DA' : phAppliedFilter ? '#2C7BE5' : '#6C757D',
                                                background: isPhInitialTableEmpty
                                                    ? 'var(--Gray-100, #F8F9FA)'
                                                    : phAppliedFilter ? '#CFE2FF' : '#FFFFFF',
                                                height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                cursor: isPhInitialTableEmpty ? 'not-allowed' : 'pointer',
                                                pointerEvents: isPhInitialTableEmpty ? 'none' : 'auto',
                                                borderRadius: '8px',
                                            }}
                                        >
                                            {isPhInitialTableEmpty ? <FilterDisabledIcon /> : phAppliedFilter ? <FilterActiveIcon /> : <FilterInactiveIcon />}
                                        </button>
                                        <FilterDrawer
                                            isOpen={phFilterPopupOpen}
                                            onClose={() => setPhFilterPopupOpen(false)}
                                            sections={phSections}
                                            tempValues={phTempValues}
                                            setTempValues={setPhTempValues}
                                            onApply={() => {
                                                const hasTime = phTempValues.changesTime_start || phTempValues.changesTime_end;
                                                const hasInit = phTempValues.initiator?.length > 0;
                                                const hasCond = phTempValues.condition?.length > 0;
                                                const hasType = phTempValues.pricingType?.length > 0;
                                                setPhAppliedFilter(hasTime || hasInit || hasCond || hasType ? {
                                                    changesTime_start: phTempValues.changesTime_start,
                                                    changesTime_end:   phTempValues.changesTime_end,
                                                    initiator:         phTempValues.initiator,
                                                    condition:         phTempValues.condition,
                                                    pricingType:       phTempValues.pricingType,
                                                } : null);
                                                setPhCurrentPage(1);
                                                setPhFilterPopupOpen(false);
                                            }}
                                            onClear={() => {
                                                setPhTempValues({
                                                    changesTime_start: null, changesTime_end: null,
                                                    initiator: [], initiatorSearch: '',
                                                    condition: [], pricingType: [],
                                                });
                                                setPhAppliedFilter(null);
                                            }}
                                             width={400}
                                        />
                                    </div>
                                </div>

                                <div className="table-wrapper" style={{ border: 'none' }}>
                                    {isLoading ? null : loadError ? (
                                        <ListLoadError
                                            what="pricing history"
                                            onRetry={() => fetchPHPage(phCurrentPage, debouncedSearch, phAppliedFilter, phSortField, phSortOrder)}
                                        />
                                    ) : isPhInitialTableEmpty ? (
                                        <div className="empty-state">
                                            <div className="empty-state-content">
                                                <div className="icon">
                                                    <img src={emptyStateImg} alt="empty" style={{ width: 120, height: 120 }} />
                                                </div>
                                                <h1 className="no-item-title mt-2">No Pricing History Found</h1>
                                                <p className="no-item-text">Nothing here yet. Initiate a Trade-In Pricing to get started!</p>
                                            </div>
                                        </div>
                                    ) : !isLoading && rows.length === 0 ? (
                                        <div className="empty-state">
                                            <div className="empty-state-content">
                                                <div className="icon">
                                                    <img src={emptySearchImg} alt="avatar" className="avatar" style={{ width: 120, height: 120, verticalAlign: 'middle' }} />
                                                </div>
                                                <h1 className="no-item-title mt-2">No History Found</h1>
                                                <p className="no-item-text">No results match what you're looking for</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <style>{`
                                                .col-resize-handle::after {
                                                    content: "";
                                                    position: absolute;
                                                    top: 50%;
                                                    transform: translateY(-50%);
                                                    height: 50%;
                                                    left: 3px;
                                                    width: 2px;
                                                    background: #DEE2E6;
                                                }
                                            `}</style>
                                            <table className="entities-table table table-striped" style={{ width: '100%', minWidth: 1100, tableLayout: 'fixed' }}>
                                                <thead>
                                                    <tr style={{ height: '45px', verticalAlign: 'middle' }}>
                                                        <SortableHeader
                                                            label="Changes Time" field="time" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort}
                                                            style={{ position: 'relative', overflow: 'hidden', width: colWidths.changesTime }}
                                                            resizeHandle={<ColResizeHandle colKey="changesTime" minWidth={120} />}
                                                        />
                                                        <SortableHeader
                                                            label="Initiator" field="initiator" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort}
                                                            style={{ position: 'relative', overflow: 'hidden', width: colWidths.initiator }}
                                                            resizeHandle={<ColResizeHandle colKey="initiator" minWidth={120} />}
                                                        />
                                                        <SortableHeader
                                                            label="Listing Model" field="listingModel" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort}
                                                            style={{ position: 'relative', overflow: 'hidden', width: colWidths.listingModel }}
                                                            resizeHandle={<ColResizeHandle colKey="listingModel" minWidth={120} />}
                                                        />
                                                        <SortableHeader
                                                            label="Listing Item" field="listingItem" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort}
                                                            style={{ position: 'relative', overflow: 'hidden', width: colWidths.listingItem }}
                                                            resizeHandle={<ColResizeHandle colKey="listingItem" minWidth={120} />}
                                                        />
                                                        <SortableHeader
                                                            label="Condition" field="condition" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort}
                                                            style={{ position: 'relative', overflow: 'hidden', width: colWidths.condition }}
                                                            resizeHandle={<ColResizeHandle colKey="condition" minWidth={100} />}
                                                        />
                                                        <SortableHeader
                                                            label="Pricing Type" field="pricingType" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort}
                                                            style={{ position: 'relative', overflow: 'hidden', width: colWidths.pricingType }}
                                                            resizeHandle={<ColResizeHandle colKey="pricingType" minWidth={100} />}
                                                        />
                                                        <th style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529', whiteSpace: 'nowrap', position: 'relative', overflow: 'hidden', width: colWidths.basePriceChanges }}>
                                                            Base Price Changes
                                                            <ColResizeHandle colKey="basePriceChanges" minWidth={120} />
                                                        </th>
                                                        <th style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529', whiteSpace: 'nowrap', position: 'relative', overflow: 'hidden', width: colWidths.deductionChanges }}>
                                                            Deduction Changes
                                                            <ColResizeHandle colKey="deductionChanges" minWidth={120} />
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows.filter(rowHasAnyChange).map((row) => {
                                                        const bothNumeric = isNumeric(row.priceBefore) && isNumeric(row.priceAfter);
                                                        const before = bothNumeric ? parseFloat(row.priceBefore) : null;
                                                        const after  = bothNumeric ? parseFloat(row.priceAfter)  : null;
                                                        const isIncrease = bothNumeric && after >= before;
                                                        const diff = bothNumeric ? Math.abs(after - before) : null;
                                                        const baseChanged = baseHasRealChange(row);
                                                        // A change to/from a text value (e.g. WhatsApp) is non-calculatable, so the
                                                        // secondary line shows "-" instead of a numeric delta (primary still shows
                                                        // the transition, e.g. "WhatsApp → SGD 100.00").
                                                        const baseNonCalculatable = isTextValue(row.priceBefore) || isTextValue(row.priceAfter);
                                                        // Only surface the View Details link when at least one deduction actually
                                                        // changed (by data, not the backend flag); otherwise show "-".
                                                        const hasRealDeductionChange = (row.deductions || []).some(deductionHasRealChange);
                                                        const isPromotion = row.pricingType === 'Promotional';
                                                        return (
                                                            <tr key={row.id}>
                                                                {/* 1. Changes Time */}
                                                                <td style={{ overflow: 'hidden' }}>
                                                                    <span style={{ whiteSpace: 'nowrap', fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                                                        {formatDateTime(row.changesTime)}
                                                                    </span>
                                                                </td>

                                                                {/* 2. Initiator */}
                                                                <td style={{ whiteSpace: 'nowrap', fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {row.initiator || '—'}
                                                                </td>

                                                                {/* 3. Listing Model */}
                                                                <td style={{ overflow: 'hidden' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                                                        <PhoneImage src={row.image} />
                                                                        <span style={{ whiteSpace: 'nowrap', fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                            {row.listingModel || '—'}
                                                                        </span>
                                                                    </div>
                                                                </td>

                                                                {/* 4. Listing Item */}
                                                                <td style={{ overflow: 'hidden' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                                                        <PhoneImage src={row.image} />
                                                                        <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                                            <span style={{ whiteSpace: 'nowrap', fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat', color: '#212529', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                                                                {row.listingItem || '—'}
                                                                            </span>
                                                                            {row.color && (
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, minWidth: 0 }}>
                                                                                    <span style={{
                                                                                        width: 12, height: 12, borderRadius: '50%',
                                                                                        background: row.colorCode,
                                                                                        border: '1.5px solid #fff',
                                                                                        boxShadow: '0 0 0 1px #000',
                                                                                        flexShrink: 0, display: 'inline-block',
                                                                                        marginLeft: 2,
                                                                                    }} />
                                                                                    <span style={{ fontSize: 10, color: '#6C757D', fontFamily: 'Montserrat', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                                        {row.color}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                {/* 5. Condition */}
                                                                <td style={{ overflow: 'hidden' }}>
                                                                    <span style={{
                                                                        height: '20px', display: 'flex', minWidth: '72px', maxWidth: '90px',
                                                                        alignItems: 'center', justifyContent: 'center',
                                                                        padding: '5px 10px', borderRadius: '99px',
                                                                        border: '1px solid #FFEB00',
                                                                        background: '#FFEB00',
                                                                        color: '#212529', fontWeight: 600, fontSize: 12,
                                                                        whiteSpace: 'nowrap', fontFamily: 'Montserrat',
                                                                        overflow: 'hidden', textOverflow: 'ellipsis',
                                                                    }}>
                                                                        {row.condition || '—'}
                                                                    </span>
                                                                </td>

                                                                {/* 6. Pricing Type */}
                                                                <td>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
                                                                        <PricingTypeBadge isPromotion={isPromotion} />
                                                                        {isPromotion && (
                                                                            <span
                                                                                data-promo-anchor
                                                                                role="button"
                                                                                tabIndex={0}
                                                                                aria-label="Campaign details"
                                                                                style={{ display: 'inline-flex', cursor: 'pointer' }}
                                                                                onMouseEnter={(e) => { if (!promoTip?.pinned) openPromoTip(e.currentTarget, row, false); }}
                                                                                onMouseLeave={() => setPromoTip(prev => (prev?.pinned ? prev : null))}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    if (promoTip?.pinned && promoTip.id === row.id) setPromoTip(null);
                                                                                    else openPromoTip(e.currentTarget, row, true);
                                                                                }}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                                                                    e.preventDefault();
                                                                                    if (promoTip?.pinned && promoTip.id === row.id) setPromoTip(null);
                                                                                    else openPromoTip(e.currentTarget, row, true);
                                                                                }}
                                                                            >
                                                                                <PromoInfoIcon />
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* 7. Base Price Changes */}
                                                                <td style={{ overflow: 'hidden' }}>
                                                                    {baseChanged ? (
                                                                        <>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat', color: '#212529', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                                {fmtPrice(row.priceBefore)}
                                                                                <ArrowRightIcon />
                                                                                {fmtPrice(row.priceAfter)}
                                                                            </div>
                                                                            {bothNumeric ? (
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, fontSize: 12, fontFamily: 'Montserrat', fontWeight: 400, whiteSpace: 'nowrap', color: isIncrease ? '#13795B' : '#B02A37', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                                    {isIncrease
                                                                                        ? <svg xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none"><path d="M6 0.75L9.5 7.875L6 6.375L2.5 7.875L6 0.75Z" fill="#13795B" stroke="#13795B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                                        : <svg xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none" style={{ transform: 'rotate(180deg)' }}><path d="M6 0.75L9.5 7.875L6 6.375L2.5 7.875L6 0.75Z" fill="#DC3545" stroke="#DC3545" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                                    }
                                                                                    SGD {diff.toLocaleString('en-SG', { minimumFractionDigits: 2 })}
                                                                                </div>
                                                                            ) : baseNonCalculatable ? (
                                                                                <Dash style={{ marginTop: 2 }} />
                                                                            ) : null}
                                                                        </>
                                                                    ) : (
                                                                        <Dash />
                                                                    )}
                                                                </td>

                                                                {/* 8. Deduction Changes */}
                                                                <td>
                                                                    {hasRealDeductionChange ? (
                                                                        <ViewDetailsLink onClick={() => { setSelectedRow(row); setDrawerOpen(true); }} />
                                                                    ) : (
                                                                        <Dash />
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                {/* Rendered outside the scrolling table wrapper so it is never clipped. */}
                                {promoTip && (
                                    <PromoPopup
                                        campaignName={promoTip.campaignName}
                                        campaignEvent={promoTip.campaignEvent}
                                        pos={promoTip}
                                    />
                                )}

                                {/* ── Pagination ── */}
                                {totalCount > 0 && (
                                    <div className="pagination" style={{ marginTop: 16 }}>
                                        <div className="pagination-info">
                                            {`${(phCurrentPage - 1) * PH_PAGE_SIZE + 1} - ${Math.min(phCurrentPage * PH_PAGE_SIZE, totalCount)} of ${totalCount} `}
                                        </div>
                                        <Pagination
                                            currentPage={phCurrentPage}
                                            totalPages={phTotalPages}
                                            onPageChange={setPhCurrentPage}
                                        />
                                    </div>
                                )}

                                {/* ── Deductions Changes Info Drawer ── */}
                                {drawerOpen && selectedRow && (
                                    <>
                                        <div className="drawer-overlay" onClick={() => { setDrawerOpen(false); setSelectedRow(null); }} />
                                        <div className="entity-drawer" ref={drawerRef} style={{ maxWidth: 591 }}>
                                            <div className="drawer-header" style={{ background: '#F8F9FA', borderBottom: '1px solid #E9ECEF',padding:'15px 20px' }}>
                                                <h3>Deductions Changes Info</h3>
                                                <button className="close-btn" onClick={() => { setDrawerOpen(false); setSelectedRow(null); }}>
                                                    <X size={18} />
                                                </button>
                                            </div>
                                            <div className="main-drawer-inner" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0px 0px 35px 0px', flexShrink: 0 }}>
                                                    <PhoneImage src={selectedRow.image} size={44} />
                                                    <div>
                                                        <div style={{ fontWeight: 700, fontSize: 18, fontFamily: 'Montserrat', color: '#212529' }}>{selectedRow.listingModel}</div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                                            <span style={{ fontSize: 13, color: '#6C757D', fontFamily: 'Montserrat' }}>
                                                                {selectedRow.listingItem}{selectedRow.color ? `, ${selectedRow.color}` : ''}
                                                            </span>
                                                            <span style={{
                                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                background: '#FFEB00',
                                                                border: '1px solid #FFEB00',
                                                                color: '#212529', fontWeight: 600, fontSize: 10,
                                                                padding: '2px 8px', borderRadius: 99, fontFamily: 'Montserrat',
                                                                whiteSpace: 'nowrap',
                                                            }}>
                                                                {selectedRow.condition}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Own scroll box: the value/formula columns can be far wider than the
                                                    591px drawer, so the table scrolls both ways with its scrollbars
                                                    pinned inside the drawer instead of below the last row. No padding
                                                    here — rows scrolling through a scroll container's padding strip
                                                    stay visible above the sticky header. */}
                                                <div style={{ padding: 0, overflow: 'auto', flex: 1, minHeight: 0 }}>
                                                    <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat', fontSize: 12 }}>
                                                        <thead>
                                                            <tr style={{ background: '#E9ECEF', borderBottom: '1px solid #E9ECEF' }}>
                                                                <th style={{ padding: '10px', fontWeight: 700, color: '#212529', whiteSpace: 'nowrap', width: 40, position: 'sticky', top: 0, background: '#E9ECEF', zIndex: 1 }}>ID</th>
                                                                <th style={{ padding: '10px', fontWeight: 700, color: '#212529', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#E9ECEF', zIndex: 1 }}>Deduction Item</th>
                                                                <th style={{ padding: '10px', fontWeight: 700, color: '#212529', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#E9ECEF', zIndex: 1 }}>Data Type Changes</th>
                                                                <th style={{ padding: '10px', fontWeight: 700, color: '#212529', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#E9ECEF', zIndex: 1 }}>Value Changes</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedRow.deductions.map((d, i) => {
                                                                const typeChanged = (d.data_type_before || d.data_type_after) && d.data_type_before !== d.data_type_after;
                                                                const valBefore = d.deduction_value_before ?? d.formula_before;
                                                                const valAfter  = d.deduction_value_after  ?? d.formula_after;
                                                                const textChanged = (valBefore != null || valAfter != null) && valBefore !== valAfter;
                                                                const resultOnlyChanged = !textChanged
                                                                    && formulaResultChanged(d.formula_result_before, d.formula_result_after);
                                                                const valChanged = textChanged || resultOnlyChanged;
                                                                // Result-only change (a column whose value shifted because a column it
                                                                // references was edited): show just the result, e.g. "40 → 90".
                                                                // Otherwise show the normal value/formula display.
                                                                const dispValBefore = resultOnlyChanged
                                                                    ? fmtDeductionValue(d.formula_result_before)
                                                                    : fmtDeductionSide(d.data_type_before, d.deduction_value_before, d.formula_before, d.formula_result_before);
                                                                const dispValAfter  = resultOnlyChanged
                                                                    ? fmtDeductionValue(d.formula_result_after)
                                                                    : fmtDeductionSide(d.data_type_after,  d.deduction_value_after,  d.formula_after,  d.formula_result_after);
                                                                return (
                                                                    <tr key={i} style={{ borderBottom: '1px solid #F1F3F5', background: i % 2 === 0 ? '#FFFFFF' : '#F8F9FA' }}>
                                                                        <td style={{ padding: '10px', color: '#212529' }}>{d.no ?? i + 1}</td>
                                                                        <td style={{ padding: '10px', color: '#212529', whiteSpace: 'nowrap' }}>{d.deduction_item_name || '-'}</td>
                                                                        <td style={{ padding: '10px', color: '#212529', whiteSpace: 'nowrap' }}>
                                                                            {typeChanged ? (
                                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                                                    {d.data_type_before || '-'}
                                                                                    <ArrowRightIcon />
                                                                                    {d.data_type_after || '-'}
                                                                                </span>
                                                                            ) : '-'}
                                                                        </td>
                                                                        <td style={{ padding: '10px', color: '#212529', whiteSpace: 'nowrap' }}>
                                                                            {valChanged ? (
                                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                                                    {dispValBefore ?? '-'}
                                                                                    <ArrowRightIcon />
                                                                                    {dispValAfter ?? '-'}
                                                                                </span>
                                                                            ) : '-'}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
